/**
 * "Your plan, in plain words" — the Retirement Village module's interpretation layer, the same
 * guided-UX pattern as the Aged Care, Downsizer and Granny Flat modules (owner direction,
 * 2026-08-25). The village deal in sentences (entry contribution, ongoing fees, the DMF taken on
 * exit), the former-home and homeowner consequences, then the pension and cashflow movements as
 * ShiftRows with driver-named whys.
 *
 * CALCULATION DISCIPLINE — every figure is quoted from what the page already computed for its
 * panels: `getVillageCostSummary`'s numbers (the same ones the cost cards show),
 * `rvInsightFinancials` (the same before/after the insight panel and Financial Impact panel use,
 * for whichever view is live — manual or the selected Suggestion Engine strategy), including its
 * own contextual pension-why and deficit narrative. Nothing is re-derived here.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { PlainWordsCard, ShiftRow, WhatMovesIt, type EstateDriver } from '../../components/narrative/PlainWords.tsx';
import { EXTRA_ALLOWABLE_AMOUNT } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { RvFundFrom } from './rvTypes.ts';
import { ASSET_CLASS_ASSUMPTIONS } from '../../../../shared/src/calculation/investmentProfiles.ts';

/** Structural mirrors of RetirementVillagePage's RvInsightFinancials / VillageCostSummary (those
 *  interfaces live in the page file; a page->recap->page type import cycle is not worth it). */
interface RvInsightFinancials {
    agePensionBefore: number;
    agePensionAfter: number;
    agePensionWhy: string;
    cashflowBefore: number;
    cashflowAfter: number;
    villageOngoingAnnual: number;
    /** Projected net worth at the end of the modelled period — the estate figure. */
    legacyEstimate: number;
    noVillageScenario: boolean;
    cashflowDeficitNarrative: string | null;
    estateTimingNote: string;
    rentAssistance: number;
}
interface VillageCostSummary {
    entryFee: number;
    annualOngoing: number;
    exitDmf: number;
    exitNetToResident: number;
}

interface RvPlanRecapProps {
    summary: VillageCostSummary;
    fin: RvInsightFinancials | null;
    fundFrom: RvFundFrom;
    rentHome: boolean;
    hasHome: boolean;
    yearsResidency: number;
    seStrategyName?: string | null;
    firstName?: string;
    /**
     * How much of the entry contribution the chosen funding does NOT cover. Anything above zero
     * means this is not yet a plan that can be executed, and the summary must say so FIRST — before
     * narrating pension and cashflow effects that assume the move happened (owner-caught,
     * 2026-08-26: "it still positions this as though this is doable").
     */
    entryFeeShortfall?: number;
    canSellHome?: boolean;
    canSellInvestmentProperty?: boolean;
    /**
     * Where the sale proceeds actually went — the same `sellHomeDetails` the engine projected.
     *
     * Without this the summary said only "funded by selling the family home" and stopped, so a plan
     * that sold a $2m home, put $300,000 into super as a pension and bought an $850,000 investment
     * property was described as if none of that had happened (owner-caught, 2026-08-26).
     */
    allocations?: Record<string, unknown> | null;
    /** Net worth today — the "before" the Financial Impact panel's estate tile uses. */
    netWorthToday?: number;
    /**
     * What the estate would be at the horizon if the client STAYED PUT — the only fair benchmark for
     * "does this move leave more or less behind". Null when it could not be computed, in which case
     * the summary states the projected estate without claiming a direction.
     */
    baselineLegacy?: number | null;
    legacyYears?: number;
    partnerFirstName?: string;
    /**
     * A lifetime annuity in the plan that returns nothing on death (owner, 24 Sep 2026: the recap
     * blamed fees and growth for an estate $2.4m behind while never saying the $2m annuity was the
     * reason). Named first among what moves the estate, and called out on its own.
     */
    nilResidualAnnuity?: { amount: number; ownerName: string } | null;
    /**
     * THE CHANGEOVER COSTS (owner, 24 Sep 2026: "the change over costs would impact the estate"
     * and the reason given did not say so). The departure fee accrued by the horizon on the contract
     * as entered — with no exit modelled, the contract is carried at what would come back — and what
     * the family home would have done staying put, so the reader can see what was given up.
     */
    dmfAtHorizon?: number | null;
    homeValueToday?: number | null;
    homeGrowthPct?: number | null;
    /**
     * The rent-the-former-home assumptions. The summary knew rent was ON but never said how much,
     * so "kept and rented out" carried no figures a client could check — the same silence the aged
     * care summary had, found in the same pass (owner-caught, 2026-08-26).
     */
    rentDetails?: { rentAmount?: number; rentExpenses?: number; partnerRent?: number } | null;
}

const FUND_FROM_PHRASES: Record<string, string> = {
    liquid: 'paid from savings and investments',
    rent_home: 'paid from savings, with the former home kept and rented out',
    sell_home: 'funded by selling the family home',
    investment_property: 'funded by selling the investment property',
};

const RvPlanRecap = ({
    summary,
    fin,
    fundFrom,
    rentHome,
    hasHome,
    yearsResidency,
    seStrategyName = null,
    firstName,
    entryFeeShortfall = 0,
    canSellHome = false,
    canSellInvestmentProperty = false,
    allocations = null,
    netWorthToday = 0,
    baselineLegacy = null,
    legacyYears = 5,
    partnerFirstName,
    nilResidualAnnuity = null,
    dmfAtHorizon = null,
    homeValueToday = null,
    homeGrowthPct = null,
    rentDetails = null,
}: RvPlanRecapProps) => {
    const weeklyRent = Math.max(0, Number(rentDetails?.rentAmount) || 0);
    const annualRent = weeklyRent * 52;
    const rentExpenses = Math.max(0, Number(rentDetails?.rentExpenses) || 0);
    const netAnnualRent = Math.max(0, annualRent - rentExpenses);
    const partnerAnnualRent = Math.max(0, Number(rentDetails?.partnerRent) || 0) * 52;
    if (!fin || fin.noVillageScenario || !(summary.entryFee > 0)) return null;

    const isUnfunded = Math.round(entryFeeShortfall) > 0;
    const fallbackRoutes = [
        canSellHome ? 'selling or downsizing the family home' : null,
        canSellInvestmentProperty ? 'selling an investment property' : null,
    ].filter(Boolean) as string[];

    const name = firstName || 'you';
    const isHomeownerByEc = summary.entryFee > EXTRA_ALLOWABLE_AMOUNT;
    // Only assert the kept-home consequence when the page's own pension-why does — the engine,
    // not this card, decides whether the former home is being counted.
    const keepsFormerHome =
        hasHome && fundFrom !== 'sell_home' && /former principal home/i.test(fin.agePensionWhy || '');
    const pensionDelta = Math.round(fin.agePensionAfter) - Math.round(fin.agePensionBefore);

    // The page already writes a contextual pension-why (rvInsightFinancials.agePensionWhy) — reuse
    // it rather than authoring a second one that could drift from the insight panel's.
    const pensionWhy =
        (fin.agePensionWhy || 'The change reflects what Centrelink counts once the entry fee is paid and the former home is dealt with.') +
        ' The Financial Impact panel shows the same before-and-after.';

    const cashflowDrivers: string[] = [];
    if (fin.villageOngoingAnnual > 0)
        cashflowDrivers.push(`the village's ongoing fees of ${formatCurrency(fin.villageOngoingAnnual)} a year start`);
    if (rentHome) {
        cashflowDrivers.push(
            netAnnualRent > 0
                ? `the former home brings in ${formatCurrency(netAnnualRent)} a year of net rent`
                : 'rent from the former home comes in',
        );
    }
    if (rentHome && partnerAnnualRent > 0) {
        cashflowDrivers.push(`${partnerFirstName || 'your partner'} pays ${formatCurrency(partnerAnnualRent)} a year in rent elsewhere`);
    }
    if (fin.rentAssistance > 0)
        cashflowDrivers.push(`${formatCurrency(fin.rentAssistance)} a year of Rent Assistance is paid`);
    if (pensionDelta !== 0)
        cashflowDrivers.push(
            `the Age Pension is ${pensionDelta > 0 ? 'higher' : 'lower'} (${formatCurrency(fin.agePensionBefore)} → ${formatCurrency(fin.agePensionAfter)} a year)`,
        );
    const cashflowWhy =
        `A full year of money in minus money out, on the same basis as the Financial Impact panel.` +
        (cashflowDrivers.length > 0 ? ` What changes it: ${cashflowDrivers.join('; ')}.` : '') +
        (fin.cashflowDeficitNarrative ? ` ${fin.cashflowDeficitNarrative}` : '');

    /*
     * WHAT THE PLAN ACTUALLY DOES WITH THE MONEY. "Funded by selling the family home" is where the
     * money came FROM; a client also needs to hear where it went, because that is the plan. Read
     * from the same allocation object the engine projected, so the sentence and the numbers cannot
     * describe different plans, and rendered only for amounts actually allocated.
     */
    const alloc = (key: string): number => {
        const parsed = parseFloat(String((allocations as Record<string, unknown> | null)?.[key] ?? ''));
        return Number.isFinite(parsed) ? parsed : 0;
    };
    const salePrice = alloc('salePrice');
    const sellingCosts = alloc('sellingCosts');
    const netProceeds = Math.max(0, salePrice - sellingCosts);
    const startsPension = (allocations as Record<string, unknown> | null)?.commenceIncomeStream === true;
    const superTotal = alloc('downsizerContribution') + alloc('partnerDownsizerContribution');
    const ipAmount = alloc('purchaseInvestmentProperty');
    const ipRent = alloc('investmentPropertyRent');
    const ipExpenses = alloc('investmentPropertyExpenses');
    const ipDuty = ipAmount > 0 ? alloc('investmentPropertyStampDuty') : 0;
    const ipState = String((allocations as Record<string, unknown> | null)?.investmentPropertyState || '');

    const destinations: string[] = [];
    if (superTotal > 0) {
        const whose = alloc('partnerDownsizerContribution') > 0 && alloc('downsizerContribution') > 0
            ? ` (split between ${name} and ${partnerFirstName || 'your partner'})`
            : '';
        destinations.push(
            `${formatCurrency(superTotal)} goes into super as a downsizer contribution${whose}${
                startsPension ? ', started as an account-based pension so its earnings are tax free' : ', left in accumulation'
            }`,
        );
    }
    if (ipAmount > 0) {
        destinations.push(
            `${formatCurrency(ipAmount)} buys an investment property${ipDuty > 0 ? ` (plus ${formatCurrency(ipDuty)} stamp duty)` : ''}${
                ipRent > 0
                    ? `, assumed to bring in ${formatCurrency(ipRent)} of rent a year${ipExpenses > 0 ? ` against ${formatCurrency(ipExpenses)} of expenses` : ''}`
                    : ''
            }`,
        );
    }
    if (alloc('purchaseAnnuity') > 0) destinations.push(`${formatCurrency(alloc('purchaseAnnuity'))} buys a lifetime annuity, of which only 60% counts in the assets test`);
    const ipGrowthPct = ASSET_CLASS_ASSUMPTIONS.propertyGrowthPct;
    const ipYieldPct = ASSET_CLASS_ASSUMPTIONS.propertyYieldPct;
    const ipExpensePct = ASSET_CLASS_ASSUMPTIONS.propertyExpenseRatioPct;
    if (alloc('purchaseShares') > 0) destinations.push(`${formatCurrency(alloc('purchaseShares'))} is invested in shares or managed funds`);
    if (alloc('giftingAmount') > 0) destinations.push(`${formatCurrency(alloc('giftingAmount'))} is gifted`);
    if (alloc('funeralBondPurchase') > 0) destinations.push(`${formatCurrency(alloc('funeralBondPurchase'))} buys a funeral bond`);
    const entryFromSale = Math.min(summary.entryFee, netProceeds);
    const leftover = Math.max(0, netProceeds - entryFromSale - superTotal - ipAmount - ipDuty - alloc('purchaseAnnuity') - alloc('purchaseShares') - alloc('giftingAmount') - alloc('funeralBondPurchase'));

    // The estate line the summary was silent on. Same pair the Financial Impact tile shows: net
    // worth today, against what is projected to be left after the modelled period.
    // Measured against STAYING PUT where that is known, because that is the decision being made.
    // Falling back to today's net worth would credit the move with five years of growth it did not
    // cause, and can report a gain where the honest comparison is a loss.
    const hasStayPut = baselineLegacy != null && Number.isFinite(baselineLegacy);
    const legacyDelta = Math.round((fin.legacyEstimate ?? 0) - (hasStayPut ? (baselineLegacy as number) : netWorthToday));
    /*
     * WHAT MOVES THE ESTATE, AS A LIST (owner, 24 Sep 2026: "very text heavy with one big
     * paragraph … why wouldn't we try and tidy this up"). One line per driver, the amount beside it
     * where the model has one, red for what leaves the estate and green for what grows it. The
     * changeover costs come first: they are what the move itself costs, whatever the money does
     * afterwards.
     */
    const legacyDrivers: EstateDriver[] = [];
    if (fundFrom === 'sell_home' && sellingCosts > 0) legacyDrivers.push({ text: 'Selling costs came off at the sale', amount: sellingCosts, sign: 'minus' });
    const dmfDriver = (dmfAtHorizon ?? 0) > 0 ? (dmfAtHorizon as number) : (summary.exitDmf > 0 ? summary.exitDmf : 0);
    if (dmfDriver > 0) legacyDrivers.push({ text: `The departure fee keeps this much of the entry contribution by year ${legacyYears}`, amount: dmfDriver, sign: 'minus' });
    if (fundFrom === 'sell_home' && (homeValueToday ?? 0) > 0) {
        // The growth the home would have had over the period, less what a property bought in its
        // place grows by — the NET effect, so the amount beside the line is the one the estate feels.
        const growthForegone = Math.round((homeValueToday as number) * (Math.pow(1 + (homeGrowthPct ?? 0) / 100, Math.max(0, legacyYears)) - 1));
        const ipGrowth = ipAmount > 0 ? Math.round(ipAmount * (Math.pow(1 + ipGrowthPct / 100, Math.max(0, legacyYears)) - 1)) : 0;
        const netGrowth = growthForegone - ipGrowth;
        legacyDrivers.push({
            text: `The ${formatCurrency(homeValueToday as number)} home is sold, so the growth it would have had at ${homeGrowthPct ?? 0}% a year is given up${ipAmount > 0 ? ` — in its place a ${formatCurrency(ipAmount)} investment property grows at ${ipGrowthPct}% a year and earns ${ipYieldPct}% gross rent (net of the two)` : ''}`,
            amount: netGrowth !== 0 ? Math.abs(netGrowth) : undefined,
            sign: netGrowth > 0 ? 'minus' : 'plus',
        });
    }
    if (nilResidualAnnuity && nilResidualAnnuity.amount > 0) legacyDrivers.push({ text: `The lifetime annuity returns nothing when ${nilResidualAnnuity.ownerName} ${nilResidualAnnuity.ownerName === 'you' ? 'die' : 'dies'} — that money leaves the estate the day it is bought; this is most of the difference`, amount: nilResidualAnnuity.amount, sign: 'minus' });
    if (superTotal > 0) legacyDrivers.push({ text: `The ${formatCurrency(superTotal)} in super grows`, sign: 'plus' });
    if (ipAmount > 0 && !(fundFrom === 'sell_home' && (homeValueToday ?? 0) > 0)) legacyDrivers.push({ text: `The ${formatCurrency(ipAmount)} investment property grows at ${ipGrowthPct}% a year and earns ${ipYieldPct}% gross rent`, sign: 'plus' });
    if (alloc('purchaseShares') > 0) legacyDrivers.push({ text: `The ${formatCurrency(alloc('purchaseShares'))} share portfolio grows`, sign: 'plus' });
    if (alloc('giftingAmount') > 0) legacyDrivers.push({ text: 'Given away, so no longer in the estate', amount: alloc('giftingAmount'), sign: 'minus' });
    if ((fin.cashflowAfter ?? 0) < 0) legacyDrivers.push({ text: 'The yearly shortfall is drawn from these assets', amount: Math.abs(fin.cashflowAfter), sign: 'minus' });
    const legacyWhy = (
        <WhatMovesIt
            lead={hasStayPut
                ? `What is projected to be left after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'}, against the ${formatCurrency(baselineLegacy as number)} that staying in the current home is projected to leave over the same period — the comparison that answers "does moving cost my estate anything". Measuring against today's wealth instead would credit the move with growth that would have happened anyway.`
                : `What is projected to be left after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'}, against what ${name} is worth today.`}
            drivers={legacyDrivers}
            closing="The entry contribution is not lost: it comes back on departure, less the departure fee, so it stays part of the estate while in residence."
        />
    );

    return (
        <PlainWordsCard eyebrow="Your plan summary" className="mt-6">
            <div className="space-y-1.5 text-sm leading-relaxed text-slate-700">
                {seStrategyName && (
                    <p className="text-xs font-medium text-slate-500">
                        Narrating the selected Suggestion Engine approach: {seStrategyName}.
                    </p>
                )}
                {/* The funding reality comes FIRST when the plan does not fund itself. Everything
                    below narrates a move that has not been paid for, and the reader has to know that
                    before they read it — otherwise the summary reads like a workable plan. */}
                {isUnfunded && (
                    <p className="rounded-lg border border-slate-200/70 border-l-4 border-l-amber-500 bg-white px-3 py-2 shadow-sm">
                        <span className="font-semibold text-slate-900">This plan is not funded yet.</span> The savings
                        allocated cover{' '}
                        <span className="font-semibold text-slate-900">{formatCurrency(Math.max(0, summary.entryFee - entryFeeShortfall))}</span>{' '}
                        of the {formatCurrency(summary.entryFee)} entry contribution, leaving{' '}
                        <span className="font-semibold text-slate-900">{formatCurrency(entryFeeShortfall)}</span> still to
                        find — and a village entry contribution has to be paid in full, so there is no part-payment
                        option the way an aged care room allows.{' '}
                        {fallbackRoutes.length > 0
                            ? <>To make this move work, {firstName || 'the client'} would need to fund the balance by <span className="font-semibold text-slate-900">{fallbackRoutes.join(' or ')}</span>. The figures below assume the move goes ahead, so treat them as what the plan WOULD look like once it is funded.</>
                            : <>There is no property here to raise the balance from, so on these numbers this village is out of reach. The figures below assume the move goes ahead regardless.</>}
                    </p>
                )}
                <p>
                    Moving in costs an <JargonTerm term="entry contribution" /> of{' '}
                    <span className="font-semibold text-slate-900">{formatCurrency(summary.entryFee)}</span>,{' '}
                    {isUnfunded ? 'only part-funded from savings so far' : (FUND_FROM_PHRASES[fundFrom] || 'paid from savings')}, plus ongoing fees of{' '}
                    <span className="font-semibold text-slate-900">{formatCurrency(summary.annualOngoing)}</span> a year.
                    {summary.exitDmf > 0 && (
                        <>
                            {' '}On leaving after {yearsResidency || 'the modelled'} years, the{' '}
                            <JargonTerm term="deferred management fee" /> keeps{' '}
                            <span className="font-semibold text-slate-900">{formatCurrency(summary.exitDmf)}</span>, returning
                            about <span className="font-semibold text-slate-900">{formatCurrency(summary.exitNetToResident)}</span>{' '}
                            to the estate.
                        </>
                    )}
                </p>
                <p>
                    Because the entry contribution is {isHomeownerByEc ? 'above' : 'at or below'} the{' '}
                    <JargonTerm term="Extra Allowable Amount" /> ({formatCurrency(EXTRA_ALLOWABLE_AMOUNT)}), Centrelink
                    treats {name} as a{' '}
                    <span className="font-semibold text-slate-900">{isHomeownerByEc ? 'homeowner' : 'non-homeowner'}</span>
                    {isHomeownerByEc
                        ? ' — the entry contribution itself is not counted in the assets test.'
                        : ' — the entry contribution is counted as an asset, but the higher asset limits apply and Rent Assistance may too.'}
                </p>
                {/* Where the money went — the plan itself, not just its funding source. */}
                {netProceeds > 0 && destinations.length > 0 && (
                    <p>
                        Selling raises{' '}
                        <span className="font-semibold text-slate-900">{formatCurrency(netProceeds)}</span> after selling
                        costs. <span className="font-semibold text-slate-900">{formatCurrency(entryFromSale)}</span> of that
                        pays the entry contribution; {destinations.join('; ')}
                        {leftover > 0 ? (
                            <>; and the remaining <span className="font-semibold text-slate-900">{formatCurrency(leftover)}</span> stays in the bank, where it is counted and deemed</>
                        ) : null}
                        .
                    </p>
                )}
                {keepsFormerHome && (
                    <p>
                        The former home is <span className="font-semibold text-slate-900">kept</span>
                        {rentHome && annualRent > 0 ? (
                            <> and <span className="font-semibold text-slate-900">rented out at {formatCurrency(weeklyRent)} a week</span> — {formatCurrency(annualRent)} a year less {formatCurrency(rentExpenses)} of expenses, so <span className="font-semibold text-slate-900">{formatCurrency(netAnnualRent)}</span> of net rent</>
                        ) : rentHome ? ' and rented out' : ''} — and unlike aged care, a retirement village move has{' '}
                        <span className="font-semibold text-slate-900">no two-year exemption</span>: Centrelink counts the
                        home straight away{rentHome ? ', with that net rent counted as income on top' : ''}. This is usually the biggest
                        driver of the pension change below.
                        {rentHome && partnerAnnualRent > 0 && (
                            <> {partnerFirstName || 'Your partner'} also pays <span className="font-semibold text-slate-900">{formatCurrency(partnerAnnualRent)} a year</span> to rent elsewhere, which comes off the household&apos;s cashflow.</>
                        )}
                    </p>
                )}
            </div>
            <div className="mt-3 border-t border-slate-100 pt-3">
                <ShiftRow variant="expanded" label="Age Pension" before={fin.agePensionBefore} after={fin.agePensionAfter} why={pensionWhy} />
                <ShiftRow variant="expanded" label="Yearly cashflow" before={fin.cashflowBefore} after={fin.cashflowAfter} why={cashflowWhy} />
                {/* The estate. The summary used to stop at cashflow, leaving the client with no idea
                    whether the move left more or less behind — which for many is the decision. */}
                {(hasStayPut || netWorthToday > 0) && (
                    <ShiftRow variant="expanded"
                        label={hasStayPut ? `Estate after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'} (vs staying put)` : `Estate after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'}`}
                        before={hasStayPut ? (baselineLegacy as number) : netWorthToday}
                        after={fin.legacyEstimate ?? 0}
                        suffix="" why={legacyWhy} />
                )}
            </div>
            {nilResidualAnnuity && nilResidualAnnuity.amount > 0 && (
                <p className="mt-3 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-slate-700">
                    <span className="font-semibold">Worth knowing.</span> This plan buys a{' '}
                    <span className="font-semibold">{formatCurrency(nilResidualAnnuity.amount)}</span> lifetime annuity with{' '}
                    <span className="font-semibold">nothing returned on death</span>: the income is guaranteed for as long as{' '}
                    {nilResidualAnnuity.ownerName} {nilResidualAnnuity.ownerName === 'you' ? 'live' : 'lives'}, and the capital does not come back to the estate. That is the main reason the estate figure above is lower.
                </p>
            )}
            {(hasStayPut || netWorthToday > 0) && (
                <p className="mt-2 text-xs leading-relaxed text-slate-600">
                    {legacyDelta >= 0 ? (
                        <>
                            On these assumptions the move leaves{' '}
                            <span className="font-semibold text-slate-900">{formatCurrency(Math.abs(legacyDelta))} more</span> in the estate than{' '}
                            {hasStayPut ? 'staying in the current home would' : `${name} is worth today`}.
                        </>
                    ) : (
                        <>
                            On these assumptions the move leaves{' '}
                            <span className="font-semibold text-slate-900">{formatCurrency(Math.abs(legacyDelta))} less</span> in the estate than{' '}
                            {hasStayPut ? 'staying in the current home would' : `${name} is worth today`} — worth weighing against what the move achieves for
                            care, cashflow and lifestyle.
                        </>
                    )}
                </p>
            )}
            {ipAmount > 0 && (
                <p className="mt-2 text-xs leading-relaxed text-slate-600">
                    <span className="font-semibold text-slate-800">Investment property assumptions:</span> rent of{' '}
                    {ipYieldPct}% of value a year ({formatCurrency(ipRent)}), {ipExpensePct}% of the rent to expenses, and growth of{' '}
                    {ipGrowthPct}% a year &mdash; the tool&apos;s standard assumptions, listed on the Assumptions page.{' '}
                    {ipDuty > 0
                        ? `Stamp duty of ${formatCurrency(ipDuty)}${ipState ? ` (${ipState} scale, no concessions)` : ''} comes out of the sale proceeds and sits in the property's cost base.`
                        : 'Stamp duty on the purchase is not modelled — no state was chosen for the purchase.'}
                </p>
            )}
            <p className="mt-1 text-[11px] text-slate-400">
                "Before" is today, at home; "after" matches the Financial Impact panel beside it.
                {fin.estateTimingNote ? ` ${fin.estateTimingNote}` : ''}
            </p>
        </PlainWordsCard>
    );
};

export default RvPlanRecap;
